<template>
  <div>
    <data-table
      ref="datatableRef"
      :config="datatableConfig"
      :data="unidades"
      :autoLoad="false"
      :refreshButton="refreshButtonConfig"
      @refresh="loadData"
      @edit-click="handleEditClick"
      @button-click="handleButtonClick">
    </data-table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useSiteUnidades } from '../../composables/useSiteUnidades';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { permissionsService } from '../../services/permissionsService';
import DataTable from '../../grid/DataTable.vue';

type UnidadeId = number | string;

interface DataTableInstance {
  loadData: () => void;
}

interface UnidadeRow {
  id: number;
  slug?: string;
  nome?: Record<string, string>;
  franquia_id?: number;
  cidade?: string;
  estado?: string;
  pais?: string;
  status?: string;
  arquivada_em?: string | null;
  [key: string]: unknown;
}

interface CellParams {
  value: unknown;
  data?: UnidadeRow;
}

interface ButtonClickData {
  action: string;
  data: UnidadeRow;
}

const emit = defineEmits<{
  edit: [unidadeId: UnidadeId];
  delete: [unidadeId: UnidadeId, unidadeData: UnidadeRow | null];
}>();

const { t } = useI18n();
const { fetchUnidades, arquivarUnidade, restaurarUnidade } = useSiteUnidades();
const { confirmAction } = useConfirmAction();

const datatableRef = ref<DataTableInstance | null>(null);
const unidades = ref<UnidadeRow[]>([]);

const franquiasMap = computed<Record<string, string>>(() => permissionsService.getFranquiasSelect());

const tableColumns = computed(() => {
  return [
    {
      data: 'id',
      title: t('ID'),
      width: 90,
      sortable: true,
      filterType: 'text',
      renderType: 'clickable_id'
    },
    {
      data: 'nome',
      title: t('Nome'),
      width: 220,
      sortable: false,
      filterType: 'text',
      cellRenderer: (params: CellParams): string => {
        const nome = params.value as Record<string, string> | undefined;
        return nome?.['pt-BR'] ?? '';
      }
    },
    {
      data: 'slug',
      title: t('Endereço na URL (slug)'),
      width: 160,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'franquia_id',
      title: t('Franquia'),
      width: 200,
      sortable: false,
      filterType: 'text',
      cellRenderer: (params: CellParams): string => {
        const franquiaId = params.value as number | undefined;
        if (franquiaId == null) return '';
        return franquiasMap.value[franquiaId] ?? String(franquiaId);
      }
    },
    {
      data: 'cidade',
      title: t('Cidade'),
      width: 140,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'estado',
      title: t('Estado'),
      width: 100,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'pais',
      title: t('País'),
      width: 100,
      sortable: true,
      filterType: 'text'
    },
    {
      data: 'status',
      title: t('Status'),
      width: 120,
      sortable: true,
      filterType: 'text',
      cellRenderer: (params: CellParams): string => {
        const status = params.value as string | undefined;
        return status === 'aberta' ? t('Aberta') : status === 'em_breve' ? t('Em breve') : (status ?? '');
      }
    },
    {
      data: 'arquivada_em',
      title: t('Arquivada'),
      width: 110,
      sortable: false,
      cellRenderer: (params: CellParams): string => {
        return params.value ? t('Sim') : t('Não');
      }
    },
    {
      data: 'id',
      title: t('Editar'),
      orderable: false,
      renderType: 'clickable_button',
      buttonText: t('Editar'),
      buttonIcon: 'pi pi-pencil',
      buttonClass: 'p-button-sm p-button-outlined',
      buttonAction: 'edit'
    },
    {
      data: 'id',
      title: t('Arquivar'),
      orderable: false,
      renderType: 'clickable_button',
      buttonText: t('Arquivar'),
      buttonIcon: 'pi pi-inbox',
      buttonClass: 'p-button-sm p-button-outlined p-button-warning',
      buttonAction: 'toggle-archive',
      buttonCondition: (row: Record<string, unknown>): boolean => !row.arquivada_em
    },
    {
      data: 'id',
      title: t('Restaurar'),
      orderable: false,
      renderType: 'clickable_button',
      buttonText: t('Restaurar'),
      buttonIcon: 'pi pi-refresh',
      buttonClass: 'p-button-sm p-button-outlined p-button-success',
      buttonAction: 'toggle-archive',
      buttonCondition: (row: Record<string, unknown>): boolean => !!row.arquivada_em
    },
    {
      data: 'id',
      title: t('Excluir'),
      orderable: false,
      renderType: 'clickable_button',
      buttonText: t('Excluir'),
      buttonIcon: 'pi pi-trash',
      buttonClass: 'p-button-sm p-button-outlined p-button-danger',
      buttonAction: 'delete'
    }
  ];
});

const datatableConfig = computed(() => {
  return {
    filename: `unidades_site_${new Date().toISOString().split('T')[0]}`,
    columns: tableColumns.value,
    rowTestId: (data: Record<string, unknown>) => {
      const id = data?.id;
      return id == null ? '' : `site-unidades-row-${id}`;
    },
    pageLength: 25,
    pagination: true,
    rowSelection: 'single',
    sortable: true,
    resizableColumns: true,
    showFilters: true,
    filterDisplay: 'row',
    stripedRows: true,
    showGridlines: true,
    size: 'small'
  };
});

const refreshButtonConfig = computed(() => {
  return {
    show: true,
    text: t('Atualizar')
  };
});

const loadData = async (): Promise<void> => {
  log('SiteUnidadesTable', 'loadData', 'Loading units');
  try {
    const items = await fetchUnidades();
    unidades.value = (items || []).map(u => ({ ...u, nome: u.nome as Record<string, string> }));
    log('SiteUnidadesTable', 'loadData', 'Loaded units', unidades.value.length);
  } catch (err) {
    const error = err as Error;
    log('SiteUnidadesTable', 'loadData', 'Error loading units', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar unidades') + ': ' + error.message);
  }
};

const handleEditClick = (unidadeId: UnidadeId): void => {
  log('SiteUnidadesTable', 'handleEditClick', 'Edit clicked for ID', unidadeId);
  emit('edit', unidadeId);
};

const handleToggleArchive = (row: UnidadeRow): void => {
  const isArchived = !!row.arquivada_em;

  const runToggle = async (): Promise<void> => {
    try {
      if (isArchived) {
        await restaurarUnidade(row.id);
        eventBus.emit('globalMessage.success', t('Unidade restaurada com sucesso'));
        eventBus.emit('site-unidades.restored');
      } else {
        await arquivarUnidade(row.id);
        eventBus.emit('globalMessage.success', t('Unidade arquivada com sucesso'));
        eventBus.emit('site-unidades.archived');
      }
      loadData();
    } catch (err) {
      const error = err as Error;
      eventBus.emit('globalMessage.error', t('Erro ao atualizar unidade') + ': ' + error.message);
    }
  };

  confirmAction({
    message: isArchived ? t('Tem certeza que deseja restaurar esta unidade?') : t('Tem certeza que deseja arquivar esta unidade?'),
    header: isArchived ? t('Restaurar Unidade') : t('Arquivar Unidade'),
    onAccept: () => { void runToggle(); }
  });
};

const handleButtonClick = (buttonData: ButtonClickData): void => {
  log('SiteUnidadesTable', 'handleButtonClick', 'Button clicked', buttonData);

  if (buttonData.action === 'edit') {
    emit('edit', buttonData.data.id);
  } else if (buttonData.action === 'delete') {
    emit('delete', buttonData.data.id, buttonData.data);
  } else if (buttonData.action === 'toggle-archive') {
    void handleToggleArchive(buttonData.data);
  }
};

const onFilterUpdated = (): void => {
  loadData();
};

const onSaved = (): void => {
  loadData();
};

const onDeleted = (): void => {
  loadData();
};

onMounted(() => {
  log('SiteUnidadesTable', 'mounted', 'Setting up event listeners');
  eventBus.on('filter.updated', onFilterUpdated);
  eventBus.on('site-unidades.saved', onSaved);
  eventBus.on('site-unidades.deleted', onDeleted);
  void loadData();
});

onBeforeUnmount(() => {
  log('SiteUnidadesTable', 'beforeUnmount', 'Cleaning up event listeners');
  eventBus.off('filter.updated', onFilterUpdated);
  eventBus.off('site-unidades.saved', onSaved);
  eventBus.off('site-unidades.deleted', onDeleted);
});

defineExpose({
  loadData
});
</script>

<style scoped>
</style>
